import { nextTheme, useTheme, type Theme } from '../lib/theme'
import { useT } from '../lib/i18n'

/**
 * The theme button in the app bar.
 *
 * It shows the theme you would GET by pressing it, not the one you are in --
 * a sun means "go light". That was already true of the two-state version and
 * matters more now there are three: light, ultra white, dark, in that order.
 *
 * The icons have to be distinguishable at 15px, so they are a sun, a filled
 * disc with a rim, and a moon. The disc is ultra white: a page, edge-lit,
 * which is exactly what that theme looks like.
 */
export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const msg = useT()
  const [theme, choose] = useTheme()
  const next = nextTheme(theme)

  return (
    <button
      type="button"
      onClick={() => choose(next)}
      title={msg(SWITCH_LABEL[next])}
      aria-label={msg(SWITCH_LABEL[next])}
      className={`flex shrink-0 items-center gap-1.5 rounded-full border border-line
                  bg-board text-ink-2 transition-colors hover:border-line-2 hover:text-ink
                  ${compact ? 'h-8 w-8 justify-center' : 'h-8 px-2.5'}`}
    >
      <ThemeGlyph theme={next} />
      {!compact && (
        <span className="font-display text-[11.5px] font-bold">
          {msg(SHORT_LABEL[next])}
        </span>
      )}
    </button>
  )
}

const SWITCH_LABEL: Record<Theme, string> = {
  light: 'theme.switchToLight',
  white: 'theme.switchToWhite',
  dark: 'theme.switchToDark',
}

const SHORT_LABEL: Record<Theme, string> = {
  light: 'theme.light',
  white: 'theme.white',
  dark: 'theme.dark',
}

export function ThemeGlyph({ theme, className = 'h-[15px] w-[15px]' }: {
  theme: Theme
  className?: string
}) {
  if (theme === 'light') {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
      </svg>
    )
  }
  if (theme === 'white') {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" />
        <circle cx="12" cy="12" r="3.4" fill="currentColor" stroke="none" opacity=".55" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 13.4A8.2 8.2 0 1 1 10.6 4a6.6 6.6 0 0 0 9.4 9.4z" />
    </svg>
  )
}
